/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { userSettingsNavigatedTopic } from '@agilelab/plugin-wb-events-common';
import {
  ConfirmDialog,
  useEnabledFeatures,
  useIsGuestUser,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbHeader,
  shouldShowTabByCommercialModules,
} from '@agilelab/plugin-wb-platform';
import { RoutedTabs } from '@mainstage/core-components';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
  useElementFilter,
} from '@mainstage/core-plugin-api';
import { useEffect } from 'react';
import { useLocation, useOutlet } from 'react-router';
import { UserSettingsAuthProviders } from './AuthProviders';
import { DataAccessTab } from './DataAccess';
import { UserSettingsGeneral } from './General';
import { USER_SETTINGS_TAB_KEY, UserSettingsTabProps } from './UserSettingsTab';
import { useTheme } from '@material-ui/core';
import { WbPage } from '@agilelab/plugin-wb-platform';

/**
 * @public
 */
export const SettingsPage = (props: { providerSettings?: JSX.Element }) => {
  const theme = useTheme();
  const { providerSettings } = props;
  const outlet = useOutlet();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const dataAccess = configApi.getOptionalBoolean(
    'mesh.profileSettings.dataAccess.enabled',
  );
  const featureRequestEnabled =
    configApi.getOptionalBoolean('mesh.marketplace.featureRequest.enabled') ??
    false;
  const { publish } = usePublishEvent({ generatorSystem: 'settings' });
  const { data: enabledFeatures, isCommercialModuleLicensed } =
    useEnabledFeatures();
  const location = useLocation();

  useEffect(() => {
    publish(userSettingsNavigatedTopic, {
      path: location.pathname,
    });
  }, [publish, location.pathname]);

  const { data: isGuestUser } = useIsGuestUser();

  const tabs = useElementFilter(outlet, elements =>
    elements
      .selectByComponentData({
        key: USER_SETTINGS_TAB_KEY,
      })
      .getElements<UserSettingsTabProps>()
      .flatMap(({ props: elemProps }) => {
        const visible = shouldShowTabByCommercialModules(
          elemProps.requiredModules,
          isCommercialModuleLicensed,
        );
        if (!visible) return [];
        if (elemProps.path === 'feature-requests' && !featureRequestEnabled) {
          return [];
        }
        return [
          {
            path: elemProps.path,
            title: elemProps.title,
            children: <>{elemProps.children}</>,
          },
        ];
      }),
  );

  const routes = [
    {
      path: '/',
      title: 'General',
      children: <UserSettingsGeneral isGuestUser={!!isGuestUser} />,
      requiredModules: [],
    },
    ...(!isGuestUser
      ? [
          {
            path: '/auth-providers',
            title: 'Authentication Providers',
            children: (
              <UserSettingsAuthProviders providerSettings={providerSettings} />
            ),
            requiredModules: [],
          },
        ]
      : []),

    ...(!isGuestUser ? tabs : []),
  ];

  if (dataAccess && !isGuestUser && enabledFeatures.marketplaceEnabled) {
    routes.push({
      path: '/data-access',
      title: 'Data Access',
      children: <DataAccessTab />,
      requiredModules: [],
    });
  }

  const searchParams = new URLSearchParams(location.search);
  const session_id = searchParams.get('session_id');
  const client_name = searchParams.get('client_name');

  const onAuthorizeRequest = async (approve: boolean) => {
    const authBackendUrl = await discoveryApi.getBaseUrl('auth');

    const response = await fetchApi.fetch(
      `${authBackendUrl}/v1/sessions/${session_id}/${
        approve ? 'approve' : 'reject'
      }`,
      {
        method: 'POST',
      },
    );

    if (!response.ok) {
      throw new Error('Unable to reject the authorization request');
    }

    const responseData = await response.json();

    const redirecUrl = responseData.redirectUrl as string;

    // Redirect to the provided URL
    window.location.href = redirecUrl;
  };

  const filteredRoutes = routes.filter(route =>
    shouldShowTabByCommercialModules(
      (route as any).requiredModules,
      isCommercialModuleLicensed,
    ),
  );

  return (
    <WbPage themeId="home">
      <WbHeader
        title="Settings and Profile"
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Settings and Profile</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      <ConfirmDialog
        title="Authorize Application"
        description={
          <span>
            Do you want to authorize{' '}
            <strong style={{ color: theme.palette.accent.main }}>
              {client_name}
            </strong>
            ?
          </span>
        }
        open={session_id && client_name ? true : false}
        onConfirm={() => onAuthorizeRequest(true)}
        onClose={() => onAuthorizeRequest(false)}
        confirmButtonText="Authorize"
      />

      <RoutedTabs routes={filteredRoutes} />
    </WbPage>
  );
};
